import React, { memo, useState, useRef, useEffect } from "react";
import { Globe, X, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { getSubdomainInfo } from "../../routes";
import { useTranslation } from "react-i18next";
import { setLanguage } from "../../store/slices/languageSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";

const Navigation: React.FC = memo(() => {
  const dispatch = useAppDispatch();
  const lang = useAppSelector((s) => s.language.lang);
  const { t } = useTranslation("landing");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(e: any) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    }
    if (mobileMenuOpen)
      document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  // Close on ESC key
  useEffect(() => {
    function handleEsc(e: any) {
      if (e.key === "Escape") setMobileMenuOpen(false);
    }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const { isSubdomainPresent } = getSubdomainInfo();

  return (
    <>
      <header className="absolute inset-x-0 top-0 z-50 bg-transparent">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <nav
            aria-label="Global"
            className="flex items-center justify-between py-4"
          >
            <div className="flex lg:flex-1">
              <a
                href="javascript:void(0)"
                className="-m-1.5 p-1.5 flex items-center"
              >
                <span className="sr-only">LiveCargo</span>
                {/* Brand mark SVG */}
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 100 100"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect
                    x="22"
                    y="38"
                    width="2"
                    height="24"
                    fill="#C97A3C"
                    opacity="0.5"
                  />
                  <rect
                    x="27"
                    y="34"
                    width="2"
                    height="32"
                    fill="#C97A3C"
                    opacity="0.6"
                  />
                  <rect
                    x="32"
                    y="30"
                    width="3"
                    height="40"
                    fill="#C97A3C"
                    opacity="0.7"
                  />
                  <rect
                    x="38"
                    y="24"
                    width="3"
                    height="52"
                    fill="#C97A3C"
                    opacity="0.85"
                  />
                  <rect x="44" y="18" width="4" height="64" fill="#E89D5C" />
                  <rect
                    x="51"
                    y="24"
                    width="3"
                    height="52"
                    fill="#C97A3C"
                    opacity="0.85"
                  />
                  <rect
                    x="57"
                    y="30"
                    width="3"
                    height="40"
                    fill="#C97A3C"
                    opacity="0.7"
                  />
                  <rect
                    x="63"
                    y="34"
                    width="2"
                    height="32"
                    fill="#C97A3C"
                    opacity="0.6"
                  />
                  <rect
                    x="68"
                    y="38"
                    width="2"
                    height="24"
                    fill="#C97A3C"
                    opacity="0.5"
                  />
                </svg>
                <span className="text-[#E89D5C] font-medium tracking-tight text-lg leading-none">
                  LiveCargo<span className="text-[#C97A3C]">.</span>
                </span>
              </a>
            </div>
            <div className="flex lg:hidden">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white"
              >
                <span className="sr-only">Open main menu</span>
                <Menu aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="hidden lg:flex lg:gap-x-12">
              <button
                onClick={scrollToTop}
                className="text-sm/6 font-normal text-[#8A9590] hover:text-white transition-colors"
              >
                {t("navigation.home")}
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="text-sm/6 font-normal text-[#8A9590] hover:text-white transition-colors"
              >
                {t("navigation.about")}
              </button>
              <button
                onClick={() => scrollToSection("builtForYou")}
                className="text-sm/6 font-normal text-[#8A9590] hover:text-white transition-colors"
              >
                {t("navigation.builtForYou")}
              </button>
              <button
                onClick={() => scrollToSection("features")}
                className="text-sm/6 font-normal text-[#8A9590] hover:text-white transition-colors"
              >
                {t("navigation.features")}
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-sm/6 font-normal text-[#8A9590] hover:text-white transition-colors"
              >
                {t("navigation.contact")}
              </button>
            </div>
            <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-4">
              <button
                onClick={() => {
                  // Normalize current language with fallback
                  const currentLang = lang === "et" ? "et" : "en";
                  const newLang = currentLang === "en" ? "et" : "en";
                  dispatch(setLanguage(newLang));
                }}
                className="flex items-center gap-x-1 text-sm/6 font-normal text-[#8A9590] hover:text-white transition-colors"
              >
                <Globe className="h-4 w-4" />
                <span>{lang === "et" ? "EST" : "EN"}</span>
              </button>

              {isSubdomainPresent && (
                <Link
                  to="/login"
                  className="inline-flex items-center space-x-2 bg-white/10 border border-white/15 text-white px-4 py-2 rounded-lg hover:bg-white/15 transition-colors text-nowrap text-sm"
                >
                  <span>{t("navigation.signIn")}</span>
                </Link>
              )}
              <button className="rounded-lg bg-[#C97A3C] space-x-2 px-4 py-2 font-medium text-white hover:bg-[#B86B30] text-sm transition-colors">
                {t("navigation.demo")}
              </button>
            </div>
          </nav>
        </div>

        {/* Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 bg-black/50 transition-opacity"></div>
        )}

        {/* Mobile Drawer */}
        <div
          ref={menuRef}
          className={`fixed inset-y-0 right-0 z-50 w-full sm:max-w-sm bg-[#0B0F0E] border-l border-white/10 p-6 shadow-lg transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between">
            <a
              href="javascript:void(0)"
              className="-m-1.5 p-1.5 flex items-center gap-3"
            >
              <span className="sr-only">LiveCargo</span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="22"
                  y="38"
                  width="2"
                  height="24"
                  fill="#C97A3C"
                  opacity="0.5"
                />
                <rect
                  x="27"
                  y="34"
                  width="2"
                  height="32"
                  fill="#C97A3C"
                  opacity="0.6"
                />
                <rect
                  x="32"
                  y="30"
                  width="3"
                  height="40"
                  fill="#C97A3C"
                  opacity="0.7"
                />
                <rect
                  x="38"
                  y="24"
                  width="3"
                  height="52"
                  fill="#C97A3C"
                  opacity="0.85"
                />
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C" />
                <rect
                  x="51"
                  y="24"
                  width="3"
                  height="52"
                  fill="#C97A3C"
                  opacity="0.85"
                />
                <rect
                  x="57"
                  y="30"
                  width="3"
                  height="40"
                  fill="#C97A3C"
                  opacity="0.7"
                />
                <rect
                  x="63"
                  y="34"
                  width="2"
                  height="32"
                  fill="#C97A3C"
                  opacity="0.6"
                />
                <rect
                  x="68"
                  y="38"
                  width="2"
                  height="24"
                  fill="#C97A3C"
                  opacity="0.5"
                />
              </svg>
              <span className="text-[#E89D5C] font-medium tracking-tight">
                LiveCargo<span className="text-[#C97A3C]">.</span>
              </span>
            </a>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-[#8A9590] p-2 rounded-md hover:bg-white/8"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="mt-6">
            <div className="space-y-2 border-t border-white/10 pt-6">
              <button
                onClick={scrollToTop}
                className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-normal text-[#8A9590] hover:text-white hover:bg-white/5"
              >
                {t("navigation.home")}
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-normal text-[#8A9590] hover:text-white hover:bg-white/5"
              >
                {t("navigation.about")}
              </button>
              <button
                onClick={() => scrollToSection("builtForYou")}
                className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-normal text-[#8A9590] hover:text-white hover:bg-white/5"
              >
                {t("navigation.builtForYou")}
              </button>
              <button
                onClick={() => scrollToSection("features")}
                className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-normal text-[#8A9590] hover:text-white hover:bg-white/5"
              >
                {t("navigation.features")}
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-normal text-[#8A9590] hover:text-white hover:bg-white/5"
              >
                {t("navigation.contact")}
              </button>
            </div>

            <div className="mt-6 border-t border-white/10 pt-6">
              {isSubdomainPresent && (
                <Link
                  to="/login"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-medium text-[#E89D5C] hover:bg-white/5"
                >
                  <span>{t("navigation.signIn")}</span>
                </Link>
              )}
            </div>
          </nav>
        </div>
      </header>
    </>
  );
});

Navigation.displayName = "Navigation";

export default Navigation;
